import { hasBankWithdrawals } from '../../util/BankWithdrawals'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'

import { PrimaryButton } from '../../components/Button'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import { InputLabel, TextInputWithLabel } from '../../components/Input'
import Modal, { ModalFooter, ModalProps } from '../../components/Modal'
import {
  AccountKindTiles,
  FieldGroup,
  FormStack,
  InlineNotice,
  LockedField,
  ModalIntro,
  ModalSection,
  RailOption,
  ResponsiveFields,
  SectionDescription,
  SectionHeading,
  SectionTitle
} from '../../components/AccountDrawerParts'
import Radom, { Address, FiatAccountDetails } from '../../state/Radom'
import User from '../../state/User'
import FiatRamps from '../../state/FiatRamps'
import { ManagedPaymentMethod, getPaymentMethods } from '../../util/Managed'
import { errorToast, successToast, useTestMode } from '../../util/Util'
import { AddressForm } from '../Verify/AddressForm'
import { Countries } from '../Verify/Countries'

type AccountModalProps = ModalProps & {
  onAccountCreated: () => void
  accountType?: AccountType
  /** A pasted wallet address the account is being created for. */
  initialCryptoAddress?: string
  /** The network of that address, when only one fits it. */
  initialCryptoMethod?: ManagedPaymentMethod
  /** A pasted IBAN the account is being created for. */
  initialIban?: string
  /** Networks a pasted address can live on; the picker offers only these. */
  allowedNetworks?: string[]
}

export enum AccountType {
  Crypto,
  Fiat
}

enum FiatAccountType {
  US = 'USD account',
  IBAN = 'EUR account',
}

const selectPlaceholder = { fontSize: 14, color: 'var(--icon-muted, #747894)' }

/**
 * Saves one of the organization's own accounts. These are held by the
 * organization's legal entity, never by anyone else: paying other people and
 * businesses goes through contacts, which carry their own beneficiary details.
 */
const CreateAccountModal = observer(({ ...props }: AccountModalProps): ReactElement => {
  const [accountName, setAccountName] = useState('')
  const [accountType, setAccountType] = useState<AccountType>(props.accountType ?? AccountType.Crypto)

  // Wallet
  const [selectedCryptoType, setSelectedCryptoType] = useState<ManagedPaymentMethod | undefined>(props.initialCryptoMethod)
  const [cryptoAddress, setCryptoAddress] = useState(props.initialCryptoAddress ?? '')

  // Bank account
  const [fiatAccountType, setFiatAccountType] = useState<FiatAccountType | undefined>(
    props.initialIban ? FiatAccountType.IBAN : undefined
  )
  const [accountOwnerAddress, setAccountOwnerAddress] = useState<Address>({} as any)
  const [iban, setIban] = useState(props.initialIban ?? '')
  const [bic, setBic] = useState('')
  const [bankName, setBankName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')
  const [bankAccountType, setBankAccountType] = useState<'CHECKING' | 'SAVINGS'>('CHECKING')
  const [bankAddress, setBankAddress] = useState<Address>({ country: 'USA', streetLine1: '', city: '' })
  const [email, setEmail] = useState('')

  const [isLoading, setIsLoading] = useState(false)
  const [testMode] = useTestMode()
  const [shouldHideMonero, setShouldHideMonero] = useState(true)

  // The registered legal name, which every account saved here is held in.
  const entityName = User.organization?.legalName?.trim() || User.organization?.name?.trim() || ''
  const entityLabel = entityName || 'your organization'

  const bankAvailability = User.organization?.bankWithdrawals
  const bankAccountsEnabled = FiatRamps.cashWithdrawalEnabled || hasBankWithdrawals(bankAvailability)
  // Both derivations are per rail, so neither applies until a rail is chosen;
  // otherwise the Bastion-only fields would show up for the wrong rail.
  const usesBastion = fiatAccountType === FiatAccountType.US
    ? !!bankAvailability?.usdAssets.length
    : fiatAccountType === FiatAccountType.IBAN && !!bankAvailability?.eurAssets.length
  const selectedRailUnavailable = fiatAccountType === FiatAccountType.US
    ? !bankAvailability?.usdAssets.length
    : fiatAccountType === FiatAccountType.IBAN &&
      !bankAvailability?.eurAssets.length && !FiatRamps.cashWithdrawalEnabled
  const completeAddress = (address: Address): boolean => !!(
    address.streetLine1?.trim() && address.city?.trim() && address.country &&
    address.postalCode?.trim() && address.subdivision?.trim()
  )
  const bastionDetailsMissing = usesBastion && (!bankName.trim() || !email.includes('@') ||
    !completeAddress(accountOwnerAddress) ||
    (fiatAccountType === FiatAccountType.US && (!/^\d{4,17}$/.test(accountNumber) ||
      !/^\d{9}$/.test(routingNumber) || !completeAddress(bankAddress))))

  useEffect(() => {
    User.getOrganization()
      .then((o) => setShouldHideMonero(!o.features?.isMoneroEnabled))
      .catch(() => setShouldHideMonero(true))
  }, [])

  useEffect(() => {
    if (selectedCryptoType && !isVisibleCryptoMethod(selectedCryptoType)) {
      setSelectedCryptoType(undefined)
    }
  }, [selectedCryptoType, shouldHideMonero])

  const isVisibleCryptoMethod = (method: ManagedPaymentMethod): boolean => {
    if (method.name === 'Monero' || method.name === 'MoneroStagenet') {
      return !shouldHideMonero
    }
    return true
  }

  const fiatAccountTypeOptions: Array<{ value: FiatAccountType, label: string, hint: string, flags: string[] }> = [
    {
      value: FiatAccountType.IBAN,
      label: 'EUR account',
      hint: 'Euro transfers to your own account, by IBAN',
      flags: ['EU', 'GB']
    },
    {
      value: FiatAccountType.US,
      label: 'USD account',
      hint: bankAvailability?.usdAssets.length ? 'US dollar transfers by wire' : 'Not enabled for this organization',
      flags: ['US']
    }
  ]
  const selectedRail = fiatAccountTypeOptions.find(option => option.value === fiatAccountType)

  const availableCryptoMethods = useMemo(() => getPaymentMethods()
    .filter((method) => !method.hostChain)
    .filter((method) => method.name !== selectedCryptoType?.name)
    .filter((method) => !method.isDisabled)
    .filter((method) => !method.isHidden)
    .filter((method) => testMode ? !!method.isTestnet : !method.isTestnet)
    .filter((method) => !method.isFiat)
    .filter((method) => !props.allowedNetworks || props.allowedNetworks.includes(method.name))
    .filter(isVisibleCryptoMethod), [selectedCryptoType?.name, testMode, shouldHideMonero, props.allowedNetworks])

  const createCryptoAccount = async (): Promise<void> => {
    if (!selectedCryptoType || !cryptoAddress) return

    setIsLoading(true)
    try {
      await Radom.createWithdrawalAccount({
        name: accountName,
        accountDetails: {
          Crypto: {
            network: selectedCryptoType.name,
            address: cryptoAddress
          }
        }
      })
      props.onAccountCreated()
      successToast('Wallet saved')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const bankDetailsComplete = !!accountName && !!fiatAccountType && !!entityName &&
    !!accountOwnerAddress.country && !!accountOwnerAddress.city && !!accountOwnerAddress.streetLine1 &&
    !(accountOwnerAddress.country === 'USA' && !accountOwnerAddress.subdivision) &&
    !(!!Countries.find(s => s.alpha3 === accountOwnerAddress.country)?.postal_code_format &&
      !accountOwnerAddress.postalCode) &&
    !(fiatAccountType === FiatAccountType.IBAN && (!iban || !bic)) &&
    !selectedRailUnavailable && !bastionDetailsMissing

  const createFiatAccount = async (): Promise<void> => {
    if (!bankDetailsComplete || !fiatAccountType) {
      errorToast('Fill in the remaining account details before saving')
      return
    }

    const accountType: FiatAccountDetails = fiatAccountType === FiatAccountType.US
      ? {
        us: {
          accountNumber,
          routingNumber,
          bankName: bankName.trim(),
          bankAccountType,
          bankAddress,
          addressV2: accountOwnerAddress
        }
      }
      : {
        iban: {
          iban: iban.replace(/\s+/g, '').toUpperCase(),
          bic: bic.trim().toUpperCase(),
          bankName: usesBastion ? bankName.trim() : undefined,
          addressV2: usesBastion
            ? accountOwnerAddress
            : { ...accountOwnerAddress, postalCode: accountOwnerAddress.postalCode || 'N/A' }
        }
      }

    setIsLoading(true)
    try {
      await Radom.createWithdrawalAccount({
        name: accountName,
        accountDetails: {
          Fiat: {
            accountOwnerName: entityName,
            accountOwnerType: 'Business',
            email: usesBastion ? email.trim() : undefined,
            accountType
          }
        }
      })
      props.onAccountCreated()
      successToast('Bank account saved')
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
    setIsLoading(false)
  }

  const createAccount = async (): Promise<void> => {
    if (accountType === AccountType.Crypto) {
      await createCryptoAccount()
    } else if (bankAccountsEnabled) {
      await createFiatAccount()
    }
  }

  const renderCryptoForm = (): ReactElement => (
    <ModalSection>
      <SectionHeading>
        <SectionTitle>Wallet</SectionTitle>
        <SectionDescription>The address and network you withdraw to.</SectionDescription>
      </SectionHeading>

      <FormStack>
        <TextInputWithLabel
          label='Name'
          placeholder='Give this wallet a name'
          disabled={isLoading}
          required
          value={accountName}
          onChange={e => setAccountName((e.target as HTMLInputElement).value)}
          onBlur={() => setAccountName(prev => prev.trim())}
        />
        <FieldGroup>
          <InputLabel>Network</InputLabel>
          <Dropdown
            disabled={isLoading}
            outerStyle={{ minHeight: 46 }}
            selectedContent={
              selectedCryptoType
                ? <div style={{ fontSize: 14, alignItems: 'center', display: 'flex', gap: 10, minHeight: 32 }}>
                  <img src={selectedCryptoType.logo} style={{ height: 20, width: 'auto', maxWidth: 20 }} />
                  <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>
                    {selectedCryptoType.ticker} · {selectedCryptoType.name}
                  </span>
                </div>
                : <span style={selectPlaceholder}>Select network</span>
            }
            dropdownContent={
              availableCryptoMethods.map(t =>
                <DropdownItem
                  key={t.name}
                  onClick={() => setSelectedCryptoType(t)}
                  style={{ fontSize: 14, alignItems: 'center', display: 'flex', gap: 10 }}>
                  <img src={t.logo} style={{ height: 20, width: 'auto', maxWidth: 20 }} />
                  <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{t.ticker} · {t.name}</span>
                </DropdownItem>
              )
            } />
        </FieldGroup>
        <TextInputWithLabel
          label='Address'
          placeholder='Paste the wallet address'
          disabled={isLoading}
          required
          value={cryptoAddress}
          onChange={e => setCryptoAddress((e.target as HTMLInputElement).value)}
          onBlur={() => setCryptoAddress(prev => prev.trim())}
        />
        <LockedField
          label='Held by'
          value={entityLabel}
          hint={`Only wallets controlled by ${entityLabel}. Radom records ${entityLabel} as the beneficiary of every ` +
            'transfer to this wallet. To pay anyone else, add them as a contact.'} />
      </FormStack>
    </ModalSection>
  )

  const renderBankForm = (): ReactElement => (
    <FormStack>
      <ModalSection>
        <SectionHeading>
          <SectionTitle>Account holder</SectionTitle>
          <SectionDescription>Your own bank accounts are held in your business&apos;s name.</SectionDescription>
        </SectionHeading>

        <FormStack>
          <TextInputWithLabel
            label='Name'
            placeholder='Give this account a name'
            disabled={isLoading}
            required
            value={accountName}
            onChange={e => setAccountName((e.target as HTMLInputElement).value)}
            onBlur={() => setAccountName(prev => prev.trim())}
          />
          <LockedField
            label='Held by'
            value={entityLabel}
            hint={`Must match the name on the bank account. Banks check the payee name, and Radom records ${entityLabel} ` +
              'as the beneficiary of every transfer. To pay anyone else, add them as a contact.'} />
          {!entityName && <InlineNotice>
            Add your organization&apos;s name in Settings before saving a bank account.
          </InlineNotice>}
          {usesBastion && <TextInputWithLabel
            label='Account holder email'
            placeholder='Where the bank can reach you'
            value={email}
            required
            disabled={isLoading}
            onChange={e => setEmail((e.target as HTMLInputElement).value)}
            onBlur={() => setEmail(prev => prev.trim())} />}
        </FormStack>
      </ModalSection>

      <ModalSection>
        <SectionHeading>
          <SectionTitle>Bank details</SectionTitle>
          <SectionDescription>Where the money is sent.</SectionDescription>
        </SectionHeading>

        <FormStack>
          <FieldGroup>
            <InputLabel>Account format</InputLabel>
            <Dropdown
              key={fiatAccountType}
              disabled={isLoading}
              outerStyle={{ minHeight: 46 }}
              selectedContent={
                selectedRail
                  ? <RailOption label={selectedRail.label} hint={selectedRail.hint} flags={selectedRail.flags} />
                  : <span style={selectPlaceholder}>EUR or USD</span>
              }
              dropdownContent={
                fiatAccountTypeOptions.map((option) =>
                  <DropdownItem
                    key={option.value}
                    onClick={() => setFiatAccountType(option.value)}
                    style={{ fontSize: 14, alignItems: 'center', display: 'flex', gap: 10, padding: '11px 12px' }}>
                    <RailOption label={option.label} hint={option.hint} flags={option.flags} />
                  </DropdownItem>
                )
              } />
          </FieldGroup>

          {fiatAccountType === FiatAccountType.IBAN && <ResponsiveFields>
            <TextInputWithLabel
              label='IBAN'
              placeholder='Enter IBAN'
              disabled={isLoading}
              required
              value={iban}
              onChange={e => setIban((e.target as HTMLInputElement).value)}
              onBlur={() => setIban(prev => prev.replace(/\s+/g, '').toUpperCase())}
            />
            <TextInputWithLabel
              label='BIC'
              placeholder='Enter BIC'
              disabled={isLoading}
              required
              value={bic}
              onChange={e => setBic((e.target as HTMLInputElement).value)}
              onBlur={() => setBic(prev => prev.replace(/\s+/g, '').toUpperCase())}
            />
          </ResponsiveFields>}

          {usesBastion && <TextInputWithLabel
            label='Bank name'
            placeholder='e.g. Chase, Wells Fargo'
            value={bankName}
            required
            disabled={isLoading}
            onChange={e => setBankName((e.target as HTMLInputElement).value)}
            onBlur={() => setBankName(prev => prev.trim())} />}

          {fiatAccountType === FiatAccountType.US && usesBastion && <>
            <ResponsiveFields>
              <TextInputWithLabel
                label='Account number'
                placeholder='Enter account number'
                value={accountNumber}
                required
                disabled={isLoading}
                onChange={e => setAccountNumber((e.target as HTMLInputElement).value.trim())} />
              <TextInputWithLabel
                label='Wire routing number'
                placeholder='9 digits'
                value={routingNumber}
                required
                disabled={isLoading}
                onChange={e => setRoutingNumber((e.target as HTMLInputElement).value.trim())} />
            </ResponsiveFields>
            <FieldGroup>
              <InputLabel>Checking or savings</InputLabel>
              <Dropdown
                disabled={isLoading}
                outerStyle={{ minHeight: 46 }}
                selectedContent={<span style={{ fontSize: 14 }}>{bankAccountType === 'CHECKING' ? 'Checking' : 'Savings'}</span>}
                dropdownContent={
                  (['CHECKING', 'SAVINGS'] as const).map(c =>
                    <DropdownItem key={c} onClick={() => setBankAccountType(c)} style={{ fontSize: 14 }}>
                      {c === 'CHECKING' ? 'Checking' : 'Savings'}
                    </DropdownItem>
                  )
                } />
            </FieldGroup>
            <SectionHeading>
              <SectionTitle>Bank address</SectionTitle>
              <SectionDescription>Use the bank address from its wire instructions.</SectionDescription>
            </SectionHeading>
            <AddressForm state={bankAddress} onUpdate={setBankAddress} requireRegion />
          </>}

          {fiatAccountType && selectedRailUnavailable && <InlineNotice>
            This account format is not enabled for your organization yet. Contact support to enable it.
          </InlineNotice>}
        </FormStack>
      </ModalSection>

      {fiatAccountType && !selectedRailUnavailable && <ModalSection>
        <SectionHeading>
          <SectionTitle>Registered address</SectionTitle>
          <SectionDescription>{entityLabel}&apos;s address, as the bank has it on file.</SectionDescription>
        </SectionHeading>
        <AddressForm state={accountOwnerAddress} onUpdate={setAccountOwnerAddress} requireRegion={usesBastion} />
      </ModalSection>}
    </FormStack>
  )

  const isFiatVerificationBlocked = accountType === AccountType.Fiat &&
    !hasBankWithdrawals(bankAvailability) && User.organization?.verificationStatus !== 'Verified'
  const showBankForm = bankAccountsEnabled && accountType === AccountType.Fiat && !isFiatVerificationBlocked

  return <Modal {...props} innerStyles={props.presentation === 'drawer' ? undefined : { width: 680 }}>
    <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 16 }}>
      <ModalIntro>
        Accounts saved here belong to {entityLabel} and are one click away in Send funds.
        To pay anyone else, add them as a contact.
      </ModalIntro>

      {props.accountType === undefined && <ModalSection>
        <SectionHeading>
          <SectionTitle>Account type</SectionTitle>
          <SectionDescription>
            A wallet on a network, or a bank account in your business&apos;s name.
          </SectionDescription>
        </SectionHeading>
        <AccountKindTiles
          value={accountType === AccountType.Crypto ? 'wallet' : 'bank'}
          onChange={kind => setAccountType(kind === 'wallet' ? AccountType.Crypto : AccountType.Fiat)}
          disabled={isLoading}
          bankDisabled={!bankAccountsEnabled}
          bankSubtitle={bankAccountsEnabled ? undefined : 'Temporarily unavailable'}
          stacked={props.presentation === 'drawer'}
          testMode={testMode} />
      </ModalSection>}

      {accountType === AccountType.Crypto && renderCryptoForm()}

      {accountType === AccountType.Fiat && !bankAccountsEnabled && <InlineNotice>
        Bank accounts are temporarily unavailable.
      </InlineNotice>}

      {bankAccountsEnabled && isFiatVerificationBlocked && <InlineNotice>
        Verify the organization to add bank accounts in live mode.
      </InlineNotice>}

      {showBankForm && renderBankForm()}
    </div>

    {accountType === AccountType.Crypto && <ModalFooter $fullWidthAction>
      <PrimaryButton
        disabled={!accountName || !selectedCryptoType || !cryptoAddress}
        isLoading={isLoading}
        style={{ width: '100%' }}
        onClick={() => { void createAccount() }}>
        Save wallet
      </PrimaryButton>
    </ModalFooter>}

    {showBankForm && <ModalFooter $fullWidthAction>
      <PrimaryButton
        disabled={!bankDetailsComplete}
        isLoading={isLoading}
        style={{ width: '100%' }}
        onClick={() => { void createAccount() }}>
        Save bank account
      </PrimaryButton>
    </ModalFooter>}
  </Modal>
})

export default CreateAccountModal
